/**
 * WS3 — shared string-builders for the premium 2026 templates' CUSTOM_CSS
 * (networkingNight / summit / soiree).
 *
 * Every builder emits the EXACT text the three templates previously inlined —
 * whitespace included — so composition stays byte-identical to the pinned
 * strings in `__tests__/premiumCss.test.ts`. If a new template needs a
 * different shape, add a parameter or keep that fragment inline in the
 * template; NEVER bend an existing builder's output.
 *
 * Conventions shared by all premium css blocks:
 *  - Only remote fetch: self-hosted woff2 fonts under `${A}/shared/fonts/`.
 *    No @import (the css-scoper drops it anyway).
 *  - All motion is gated behind `prefers-reduced-motion: no-preference`;
 *    static poster/paper surfaces remain without motion.
 *  - Section DOM ids = CraftJS node ids (`#<prefix>_<section>`), so
 *    scroll-reveal can target them directly.
 */
import { A } from "../temp";

/** The shared entrance/reveal easing (expo-out style). */
export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export interface PremiumFontFace {
  /** css font-family name, e.g. "Hanken Grotesk". */
  family: string;
  /** woff2 basename (no extension) under `${A}/shared/fonts/`. */
  file: string;
  /** Variable-font weight range; defaults to "100 900". */
  weightRange?: string;
  /** Face style; defaults to "normal" (WS4-B2: lora-italic et al.). */
  style?: "normal" | "italic";
}

/**
 * Self-hosted @font-face blocks (font-display: swap, single woff2 source),
 * joined by a single newline.
 */
export const fontFaces = (faces: PremiumFontFace[]): string =>
  faces
    .map(
      (f) => `@font-face {
  font-family: '${f.family}';
  font-style: ${f.style ?? "normal"};
  font-weight: ${f.weightRange ?? "100 900"};
  font-display: swap;
  src: url("${A}/shared/fonts/${f.file}.woff2") format("woff2");
}`,
    )
    .join("\n");

/**
 * Declaration boilerplate every premium hero rule starts with: own stacking
 * context + clipped decorative overflow. Use inside a `#<id>_hero { … }` rule.
 */
export const heroShell = `  position: relative;
  isolation: isolate;
  overflow: hidden;`;

/**
 * Declaration boilerplate for decorative hero pseudo-elements (::before /
 * ::after): non-interactive, behind the content. Use inside the rule body.
 *
 * z-index is 1, NOT 0: the hero's inner content div (positioned, z-auto,
 * own background) paints AFTER the section's pseudo-elements in tree order,
 * so z-index:0 ornaments were buried under it in 11/13 templates. z-index:1
 * lifts the ornament above the background/overlay divs while staying under
 * the hero content (motion container at z-10). Deliberate visual fix — the
 * byte-identity pins in `__tests__/premiumCss.test.ts` were updated with it.
 */
export const decorLayer = `  content: "";
  position: absolute;
  z-index: 1;
  pointer-events: none;`;

/**
 * The shared `<prefix>FadeUp` keyframes (opacity + translateY entrance),
 * indented for use INSIDE `motionGate`.
 */
export const fadeUpKeyframes = (prefix: string, distancePx: number): string =>
  `  @keyframes ${prefix}FadeUp {
    from { opacity: 0; transform: translateY(${distancePx}px); }
    to { opacity: 1; transform: translateY(0); }
  }`;

/**
 * Wraps already-indented rules in the reduced-motion media gate. `inner` must
 * NOT carry leading/trailing newlines — the gate supplies them.
 */
export const motionGate = (inner: string): string =>
  `@media (prefers-reduced-motion: no-preference) {
${inner}
}`;

/**
 * Scroll-reveal block (for use INSIDE `motionGate`): sections fade up via
 * `animation-timeline: view()` when supported. Selectors are built as
 * `#<prefix>_<section>` and wrapped 5 per line, exactly like the hand-written
 * originals. The animation reuses the `<prefix>FadeUp` keyframes.
 */
export const revealBlock = (prefix: string, sections: string[], rangeEndPct: number): string => {
  const selectors = sections.map((s) => `#${prefix}_${s}`);
  const lines: string[] = [];
  for (let i = 0; i < selectors.length; i += 5) {
    lines.push(selectors.slice(i, i + 5).join(", "));
  }
  return `  /* Scroll-reveal: content sections fade up as they enter the viewport */
  @supports (animation-timeline: view()) {
    ${lines.join(",\n    ")} {
      animation: ${prefix}FadeUp 1ms ${EASE} both;
      animation-timeline: view();
      animation-range: entry 0% entry ${rangeEndPct}%;
    }
  }`;
};
